<template>
  <div class="flex items-center">
    <div v-show="!editing" @click="edit" class="name-input-display text-ellipsis whitespace-nowrap overflow-hidden">
      {{ initialValue }}
    </div>
    <div v-show="editing" class="border-b-2">
      <!-- keypress.enter triggers a blur so that we only submit once -->
      <input
        @keydown.enter="editing = false"
        @blur="submit"
        ref="input"
        type="text"
        :value="inputDisplayName"
        @input="name = getValue($event)"
        class="outline-offset-4 outline-primary-active bg-grey-950 rounded-t text-sm px-1.5 py-1v"
        :aria-label="`Entrez le nom du ${type}`"
      />
    </div>
    <PButton
      v-if="!readonly && !hideEditBtn && !showSaved"
      size="icon"
      mode="tertiary-borderless"
      @click="edit"
      class="ml-1"
      :aria-label="`Modifier le nom du ${type}`"
    >
      <OhVueIcon :name="editIcon" class="text-blue-france-sun-113" />
    </PButton>
    <SaveFieldStatus
      v-if="showSaved && !hideSaveStatus"
      :loading="asyncOperation.loading.value"
      scale="1.2"
      class="ml-2"
      load-size="sm"
    />
  </div>
</template>

<script setup lang="ts">
import { type Nullable, getValue, useIcons, PButton } from "@pasta/box-v2";
import { RiEditFill } from "oh-vue-icons/icons";
import { computed, nextTick, ref } from "vue";
import { OhVueIcon } from "oh-vue-icons";
import SaveFieldStatus from "./SaveFieldStatus.vue";
import { useAsyncOperation } from "../composable/useAsyncOperation";
import { FormOperation } from "../types";

interface Props {
  initialValue: string;
  readonly?: boolean;
  type: "document" | "classeur";
  hideEditBtn?: boolean;
  /** when the parent already displays the save status of this name */
  hideSaveStatus?: boolean;
  id: number;
}
const props = defineProps<Props>();
const emit = defineEmits<{
  (e: "submit", value: string): void;
}>();
const [editIcon] = useIcons(RiEditFill);

const name = ref<string>(props.initialValue);
const inputDisplayName = computed(() => {
  return /^Nouveau classeur\s?(\((\d+)\))?$/.test(props.initialValue) ? "" : props.initialValue;
});
const input = ref<Nullable<HTMLInputElement>>(null);

const editing = ref<boolean>(false);
async function edit() {
  if (props.readonly) return;
  editing.value = true;
  await nextTick();
  input.value!.focus();
}

function submit() {
  editing.value = false;
  emit("submit", name.value);
  name.value = props.initialValue;
}

// save status
const asyncOperation = useAsyncOperation();
const showSaved = asyncOperation.getShowSaved(
  `${props.type === "document" ? FormOperation.DOCUMENT_NAME : FormOperation.FOLDER_NAME}-${props.id}`
);
</script>
